import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { initQuestions } from "../../functions/setUserQuestions";
import { selectAdminToken, selectSessionId } from "../../admin/sessionSlice";
import { processQuestions } from "../../admin/questionSlice";
import {
  loadBundledAvatarUrls,
  getWmlAvatarDisplayUrls,
} from "../../lib/wmlAvatars";
import "./selectCharacter.css";

const LEGACY_WML_AVATAR_KEY = "wmlAvatarKey";

function scopedAvatarKey(sessionId, theme) {
  if (!sessionId || !theme) return null;
  return `wmlAvatar:${sessionId}:${theme}`;
}

export default function SelectCharacter() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentTheme, data: themeData } = useSelector((s) => s.theme);
  const { user } = useSelector((s) => s.auth);
  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const [selected, setSelected] = useState(null);
  const initRequestedRef = useRef(false);

  const bundled = useMemo(() => loadBundledAvatarUrls(), []);
  const { urls: avatars } = useMemo(
    () => getWmlAvatarDisplayUrls(themeData, bundled),
    [themeData, bundled]
  );

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (initRequestedRef.current) return;
    if (!themeData || !user || !adminToken) return;
    initRequestedRef.current = true;

    dispatch(processQuestions())
      .unwrap()
      .then(() =>
        initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
        })
      )
      .catch((err) => {
        console.error("Error in process->init chain:", err);
      });
  }, [dispatch, themeData, user, adminToken]);

  useEffect(() => {
    const key = scopedAvatarKey(sessionId, currentTheme);
    if (!key) return;
    try {
      const existing = sessionStorage.getItem(key);
      if (existing) {
        navigate("/who-most-likely", { replace: true });
      }
    } catch {
      /* ignore */
    }
  }, [navigate, sessionId, currentTheme]);

  const handleSubmit = () => {
    if (selected === null) return;
    const key = scopedAvatarKey(sessionId, currentTheme);
    if (!key) return;

    try {
      const existing = sessionStorage.getItem(key);
      if (existing) {
        navigate("/who-most-likely", { replace: true });
        return;
      }
    } catch {
      /* ignore */
    }

    const avatarKey = String(selected + 1);
    try {
      sessionStorage.setItem(key, avatarKey);
      sessionStorage.setItem(LEGACY_WML_AVATAR_KEY, avatarKey);
    } catch {
      /* ignore quota / private mode */
    }
    navigate("/who-most-likely", { state: { avatarIndex: selected } });
  };

  return (
    <div className="select-character-page">
      <h1 className="select-character-title">Select your Character</h1>
      <div className="select-character-grid">
        {avatars.map((src, index) => (
          <button
            key={index}
            type="button"
            className={`select-character-tile${
              selected === index ? " select-character-tile--selected" : ""
            }`}
            onClick={() => setSelected(index)}
            aria-pressed={selected === index}
          >
            <img src={src} alt="" />
          </button>
        ))}
      </div>
      <div className="select-character-footer">
        <button
          type="button"
          className="select-character-submit"
          onClick={handleSubmit}
          disabled={selected === null}
        >
          Submit
        </button>
      </div>
    </div>
  );
}
